<template>
  <div :key="`action-menu-${actionId}`" ref="el">
    <item-header type="action" :choosable="true" :selected-id="actionId" @select="selectAction" @close="$emit('close')">
      <template #content>
        <editable-item-title :item="action" />
      </template>
    </item-header>

    <drawer
      v-model:open="drawerStates.actionGeneral"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-text
          ref="titleField"
          :label="$t('labels.title')"
          :value="action.title"
          @enter="descriptionField?.focus()"
          @update:value="updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :label="$t('labels.description')"
          :value="action.description"
          @update:value="updateKey('description', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-model:open="drawerStates.actionCosts"
      :title="$t('labels.costs')"
    >
      <action-costs-list
        :action-id="action.id"
        :costs="action.costs.filter(notNull)"
        @update:costs="setCosts($event)"
        @copy="emit('copy', $event)"
      />
    </drawer>

    <drawer
      v-model:open="drawerStates.actionEffects"
      :title="$t('objectNames.effect', 2)"
    >
      <effect-list
        ref="effectList"
        :effects="effects"
        :selected-effect="selectedEffect"
        @select="select($event)"
        @copy="emit('copy', $event)"
      >
        <item-combo-select
          key="actionmenu-effect-select"
          :selection="affectedItems"
          :available-ids="availableEffectElementIds"
          :targets="targets"
          :resources="resources"
          :indices="indices"
          :hide-selected="true"
          :include-null="false"
          :types="['target', 'resource']"
          @select-single="onAffectedItemChoose"
          @choose="onAffectedItemsChoose"
        />
      </effect-list>
    </drawer>

    <drawer
      v-model:open="drawerStates.actionRequirements"
      :title="$t('objectNames.requirement', 2)"
    >
      <requirement-item-list
        :item-type="action.type"
        :item-id="action.id"
        @select="select($event)"
        @deselect="deselect($event)"
        @copy="emit('copy', $event)"
      />

      <div class="label mt-4">
        {{ $t("labels.frequency") }}
      </div>
      <i18n-t keypath="texts.maxUses" tag="span" scope="global">
        <input-number
          :min="1"
          :allow-null="true"
          :value="action.maxOccurrences ?? undefined"
          :icons="action.maxOccurrences != null ? ['x'] : []"
          class="input_inline input_small"
          placeholder="∞"
          @click:x="updateKey('maxOccurrences', null)"
          @update:value="updateKey('maxOccurrences', $event)"
        />
      </i18n-t>
    </drawer>

    <drawer
      v-model:open="drawerStates.actionVisibility"
      :title="$t('labels.visibility')"
    >
      <div class="column gap-4">
        {{ $t("questions.showActionIf") }}
        <input-checkbox
          :label="$t('labels.hiddenIfCostsNotCovered')"
          :value="action.hiddenIfCostsNotCovered"
          @update:value="updateKey('hiddenIfCostsNotCovered', $event)"
        />
        <input-checkbox
          :label="$t('labels.hiddenIfRequirementsNotFulfilled')"
          :value="action.hiddenIfRequirementsNotFulfilled"
          @update:value="updateKey('hiddenIfRequirementsNotFulfilled', $event)"
        />
        <input-checkbox
          :label="$t('labels.hiddenIfMaxUsesReached')"
          :value="action.hiddenIfMaxUsesReached"
          @update:value="updateKey('hiddenIfMaxUsesReached', $event)"
        />
      </div>
      <h3 class="label mt-5">{{ $t("labels.effectVisibility") }}</h3>
      <div class="column gap-1">
        <input-radio
          :value="action.effectsVisibility"
          :data="EFFECTS_VISIBILITY.asIs"
          :label="$t('labels.effectAsIs')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="action.effectsVisibility"
          :data="EFFECTS_VISIBILITY.visible"
          :label="$t('labels.effectForceVisible')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="action.effectsVisibility"
          :data="EFFECTS_VISIBILITY.valuesHidden"
          :label="$t('labels.effectValuesHidden')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
        <input-radio 
          :value="action.effectsVisibility"
          :data="EFFECTS_VISIBILITY.hidden"
          :label="$t('labels.effectForceHidden')"
          @update:value="updateKey('effectsVisibility', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-model:open="drawerStates.actionDependingIncidents"
      :title="$t('objectNames.dependentIncident', 2)"
    >
      <depending-incident-triggers
        :activator="action"
        :selected-condition="selectedCondition"
        @select="onDependingIncidentSelect($event)"
      />
    </drawer>

    <drawer
      v-model:open="drawerStates.actionScenarios"
      :title="$t('objectNames.scenario', 2)"
    >
      <div class="column">
        <input-checkbox
          v-for="scenario of scenarioStore.orderedItems"
          :key="scenario.id"
          :label="scenario.title"
          :value="scenario.actionIds.includes(action.id)"
          @update:value="
            $event
              ? scenarioStore.connectAction({
                  id: scenario.id,
                  actionId: action.id
                })
              : scenarioStore.disconnectAction({
                  id: scenario.id,
                  actionId: action.id
                })
          "
        />
      </div>
    </drawer>

    <drawer v-model:open="drawerStates.actionStakeholders" title="Stakeholder">
      <stakeholder-list
        item-type="action"
        :item-id="action.id"
        @select="selectUid($event.uid)"
        @deselect="deselectUid($event.uid)"
      />
    </drawer>

    <drawer
      v-if="features.design"
      v-model:open="drawerStates.actionDesign"
      :title="$t('labels.design')"
    >
      <input-image
        :id="action.imageId ?? undefined"
        :max-width="1000"
        :max-height="1000"
        :compression="0.9"
        :control-object-fit="true"
        :target-width="325"
        :target-height="520"
        @update="setImage"
        @delete="deleteImage"
      />
    </drawer>

    <menu-group>
      <button
        class="button button_secondary mt-2"
        type="button"
        @click="deleteAction"
      >
        {{ $t("actions.deleteAction", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick, useTemplateRef } from "vue";
import { notNull } from "@/helpers/utility/Type";
import { map } from "lodash-es";
import { isStoreItemOfType } from "../ItemOfStore";

import InputText from "@core/input/InputText.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputRadio from "@core/input/RadioButton.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputImage from "@editor/items/image/InputImage.vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import Drawer from "@core/ui/DrawerBlock.vue";

import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";

import EffectList from "@editor/items/effect/EffectList.vue";
import ActionCostsList from "@editor/items/action/ActionCostsList.vue";
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";
import RequirementItemList from "@editor/items/requirement/RequirementItemList.vue";
import DependingIncidentTriggers from "@editor/items/incident/DependingIncidentTriggers.vue";

import { useActionStore } from "@editor/items/action/actions.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useIndexStore } from "../index/indices.store";
import { useFeatures } from "@editor/store/features.store";

import type { ActionItem } from "./ActionItem";
import type { EffectItem } from "../effect/EffectItem";
import type { ConditionItem } from "../condition/ConditionItem";
import type { IncidentItem } from "../incident/IncidentItem";
import type { RequirementItem } from "../requirement/RequirementItem";
import type { ActionCostCopyItem, ActionCostItem } from "./ActionCostItem";
import { splitUid, type Uid } from "@/helpers/utility/Id";

interface Props {
  actionId: number;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (event: "select", item: EffectItem | ConditionItem | { type: "stakeholder"; id: number } | { type: "requirement"; id: number }  | null): void;
  (event: "deselect", item: EffectItem | ConditionItem | { type: "stakeholder"; id: number } | { type: "requirement"; id: number } ): void;
  (event: "select-main", item: IncidentItem | ActionItem | null): void;
  (event: "delete"): void;
  (event: "close"): void;
  (event: "copy", item: EffectItem | ConditionItem | RequirementItem | ActionCostCopyItem): void;
}>();

const el = ref<HTMLElement>();
const titleField = ref<InstanceType<typeof InputText>>();
const descriptionField = ref<InstanceType<typeof InputTexteditor>>();
const effectList = useTemplateRef("effectList");

const features = useFeatures();
const drawerStates = useDrawerStates();
const scenarioStore = useScenarioStore();
const actionStore = useActionStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();

const action = computed(() => {
  return actionStore.items[props.actionId];
});
const targets = computed(() => targetStore.items);
const indices = computed(() => useIndexStore().items);
const effects = computed(() => useEffectStore().byAction[props.actionId] ?? []);

const affectedItems = computed(() => {
  return effects.value
    .map((effect) => {
      if (effect.affectedType === "resource") {
        return resources.value[effect.affectedId];
      } else if (effect.affectedType === "target") {
        return targets.value[effect.affectedId];
      }
    })
    .filter(notNull);
});
const resources = computed(() => resourceStore.items);
// const resourcesSize = computed(() => resourceStore.length);
// const resourcesOrder = computed(() => resourceStore.order);

const subSelection = computed(() => {
  return useVariablesViewStore().subSelection;
});
const selectedCondition = computed(() => {
  return subSelection.value?.type === "condition"
    ? (subSelection.value as { type: "condition"; id: number })
    : undefined;
});
const selectedEffect = computed(() => {
  return subSelection.value?.type === "effect"
    ? (subSelection.value as { type: "effect"; id: number })
    : undefined;
});

const availableEffectElementIds = computed(() => {
  return {
    target: map(targets.value, (target) => target.id),
    resource: map(resources.value, (resource) => resource.id),
    index: []
  }
});

watch(
  () => props.actionId,
  () => {
    if (el.value != null) focusTitleIfEmpty();
  },
  { immediate: true }
);

onMounted(() => {
  focusTitleIfEmpty();
});

function updateKey<K extends keyof ActionItem>(key: K, value: ActionItem[K]) {
  actionStore.update({
    id: action.value.id,
    [key]: value
  });
}
function focusTitleIfEmpty() {
  if (action.value.title.length === 0) {
    drawerStates.actionGeneral = true;
    nextTick(() => {
      titleField.value?.focus?.();
    });
  }
}

function select(subSelection: EffectItem | ConditionItem | { type: "requirement"; id: number } | { type: "stakeholder"; id: number } | null) {
  emit("select", subSelection);
}

function deselect(subSelection: EffectItem | ConditionItem | { type: "requirement"; id: number } | { type: "stakeholder"; id: number }) {
  emit("deselect", subSelection);
}

function selectAction(actionId: number | null) {
  if (actionId === null) {
    emit("close");
    return;
  }
  emit("select-main", actionStore.items[actionId]);
}

function setCosts(costs: ActionCostItem[]) {
  actionStore.setCosts({
    id: props.actionId,
    costs: costs
  });
}

function deleteAction() {
  emit("delete");
  actionStore.delete({
    id: props.actionId
  });
}
function setImage(image: { id: number }) {
  updateKey("imageId", image.id);
}
function deleteImage() {
  updateKey("imageId", null);
}

async function onAffectedItemChoose(item: {
  type: "target" | "resource";
  id: number;
} | null) {
  if (item == null) {
    return;
  }
  
  const effect = await useEffectStore().create({
    activatorType: action.value.type,
    activatorId: action.value.id,
    affectedType: item.type,
    affectedId: item.id
  });
  if (effectList.value) {
    effectList.value.focusEffectExpressionField(effect);
  }
}
function onAffectedItemsChoose(
  items: { type: "target" | "resource"; id: number }[]
) {
  useEffectStore().connectActivatorEffects(action.value, items);
}

function onDependingIncidentSelect(item: ConditionItem | IncidentItem) {
  if (isStoreItemOfType(item, "condition")) {
    emit("select", item);
  } else {
    drawerStates.incidentTriggers = true;
    emit("select-main", item);
  }
}

function selectUid<T extends "stakeholder" | "requirement">(uid: Uid<T>) {
  emit("select", splitUid(uid));
}
function deselectUid<T extends "stakeholder" | "requirement">(uid: Uid<T>) {
  emit("deselect", splitUid(uid));
}

const EFFECTS_VISIBILITY = {
  asIs: "as-is",
  hidden: "hidden",
  visible: "visible",
  valuesHidden: "values-hidden"
} as const;
</script>

<style lang="scss"></style>
